import React, {useEffect, useMemo, useRef, useState} from "react";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Button,
    CircularProgress,
    Stack,
    Typography,
    useMediaQuery,
    useTheme
} from "@mui/material";
import {useTranslation} from "react-i18next";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import {
    Reservation
} from "crqlar-react-common/dist/models/reservation/reservation.model";
import {Profile} from "crqlar-react-common/dist/models/profile/profile.model";
import {
    ProfileService
} from "crqlar-react-common/dist/services/profile/profile.service";
import {ReservationService} from "crqlar-react-common/dist/services/reservation/reservation.service";
import {CommonThemeColors} from "crqlar-react-common/dist/components/theme";
import {getProfileFullName, findProfileById} from "crqlar-react-common/dist/utils/profile.util";
import ReservationProfile
    from "crqlar-react-common/dist/components/profilePreview/ReservationProfile";
import CommonConfirmDialog from "crqlar-react-common/dist/components/dialog/CommonConfirmDialog";
import {
    useShowNewProfile
} from "crqlar-react-common/dist/components/profilePreview/newProfileToggle";
import GuestSearch, {
    NoGuests
} from "crqlar-react-common/dist/components/profilePreview/guests/GuestSearch";
import ProfileMatcher, {
    QuickAddProfileProps
} from "crqlar-react-common/dist/components/profilePreview/ProfileMatcher.component";
import {
    useProfilesQuery
} from "crqlar-react-common/dist/utils/hooks/profile/useProfiles";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";
import {thinScrollbarSx} from "crqlar-react-common/dist/utils/scrollbar.util";
import EnteredOnline from "./EnteredOnline";

interface Props {
    fullProfilePreview?: Profile,
    setFullProfilePreview?: (profile?: Profile) => void,
    reservation?: Reservation,
    setReservation?: (reservation: Reservation) => void
    open?: boolean
    onCreateProfile?: (newProfile: Profile) => void
    expandOnMount?: boolean
    maxAllowedCount?: number

    // weekly planner profiles:
    serviceProfileIds?: string[]
    addProfile?: (profile: Profile) => void
    removeProfileId?: (profileId: string) => void

    // close accordion on profile add/remove
    closeAccordionOnChange?: boolean

    /**
     * Fired when the user picks a guest from the list. Guests of one group can have different
     * stays (own check-out date, own room, own restaurant), so the dialog recalculates itself
     * against the picked guest. Only user-driven picks fire it - the accordion that opens on
     * mount must not overwrite a draft that was prefilled for the whole group.
     */
    onProfileSelected?: (profileId: string) => void

    /** The dialog's own close: the full profile shows it as an X next to Copy, like the CRM profile. */
    onClose?: () => void
}

const ReservationProfiles = ({
                                 fullProfilePreview,
                                 setFullProfilePreview,
                                 reservation,
                                 setReservation,
                                 open,
                                 onCreateProfile,
                                 expandOnMount = true,
                                 maxAllowedCount,
                                 serviceProfileIds,
                                 addProfile,
                                 removeProfileId,
                                 closeAccordionOnChange = false,
                                 onProfileSelected,
                                 onClose,
                             }: Props) => {

    const profileIds = [
        ...(reservation?.profileIds ?? []),
        ...(serviceProfileIds ?? [])
    ]

    const {
        data,
        isFetching,
        refetch,
    } = useProfilesQuery(profileIds)
    const {t} = useTranslation()
    // Read-only users may view the attached profiles, but not add/attach new ones.
    const readOnly = useReadOnly()
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down('md'))
    const [chosenProfile, setChosenProfile] = useState<Profile>(data?.at(0) || {})
    const [chosenProfileId, setChosenProfileId] = useState<string>(chosenProfile.id || '')
    const [expanded, setExpanded] = useState<string | undefined>(undefined)
    const fullMode = !!fullProfilePreview
    const [searchOpen, setSearchOpen] = useState(false)
    // The new sidebar asks before a guest leaves the reservation; the old one removes at once, as before.
    const [removing, setRemoving] = useState<Profile>()
    // The new sidebar shows one guest and lists the others at its bottom; the old one has an accordion per guest.
    const showNewProfile = useShowNewProfile()
    const scrollRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        // open the search if we do not have any profiles in that reservation
        if (data === undefined || isFetching === true) return

        if (data.length === 0) {
            setSearchOpen(true)
        }

    }, [isFetching, data]);


    const fetchProfile = async () => {

        try {
            if (chosenProfileId) {
                const tmpProfile = await ProfileService.getProfileById(chosenProfileId)
                setChosenProfile(tmpProfile)
            }
        } catch (e) {
            console.log(e)
        }
    }

    useEffect(() => {
        fetchProfile()
    }, [chosenProfileId])


    useEffect(() => {
        if (!expandOnMount) return
        if (isMobile) return
        if (!expanded && !isFetching) {
            // The reservation's first guest (its main guest), not whichever profile the list request returned first.
            setExpanded(findProfileById(data ?? [], profileIds[0])?.id || data?.at(0)?.id || '')
        }
    }, [isFetching, open, isMobile]);

    useEffect(() => {
        if (!expandOnMount) return
        if (!open) {
            setExpanded(undefined)
        }
    }, [open]);

    // Close accordion when profiles are added or removed if closeAccordionOnChange is true
    useEffect(() => {
        if (closeAccordionOnChange) {
            setExpanded(undefined)
        }
    }, [profileIds.length, closeAccordionOnChange]);

    const handleChange = (profileId: string) => () => {
        if (expanded === profileId) {
            setExpanded(undefined)
        } else {
            setExpanded(profileId)
        }
        setChosenProfileId(profileId || '')

        // Collapsing keeps the last pick active - the form stays on the guest it was
        // recalculated for instead of jumping back to the group.
        if (profileId) {
            onProfileSelected?.(profileId)
        }
    };


    const selectGuest = (profileId: string) => {
        setExpanded(profileId)
        setChosenProfileId(profileId)
        onProfileSelected?.(profileId)
        // The picked guest's profile starts at its top, not where the list of guests was.
        scrollRef.current?.scrollTo({top: 0})
    }

    // The new guest search: picking a guest who is already on the booking only shows them.
    const pickGuest = (profile: Profile) => {
        if (!profile.id) return
        if (!profileIds.includes(profile.id)) {
            setReservation?.({
                ...reservation,
                profileIds: [...profileIds, profile.id]
            })
            addProfile?.(profile)
        }
        setSearchOpen(false)
        setExpanded(profile.id)
        onProfileSelected?.(profile.id)
    }

    const removeGuest = (profile: Profile) => showNewProfile ? setRemoving(profile) : handleDeleteProfile(profile)

    const handleDeleteProfile = (profile: Profile) => {
        const updatedRes: Reservation = {
            ...reservation,
            // Keeps the reservation's order (not the profile request's), so the first guest stays the main guest.
            profileIds: (reservation?.profileIds ?? []).filter(profileId => profileId !== profile.id)
        }

        setReservation?.(updatedRes)
        removeProfileId?.(profile.id!)
    }

    const handleCreateNewGuest = async (props: QuickAddProfileProps) => {
        try {
            const response = await ProfileService.createProfile({
                firstName: props.firstName,
                lastName: props.lastName,
                emails: props.email ? [
                    {
                        email: props.email
                    }
                ] : [],
                phones: props.phone ? [
                    {
                        phoneNumberFull: props.phone
                    }
                ] : []
            });

            onCreateProfile?.(response)
            setSearchOpen(false)

            return response;
        } catch (error) {
            console.error('Error creating profile:', error);
        }
    };

    const renderNoProfilesUI = () => {
        return (
            <Box
                sx={{
                    display: 'flex',
                    padding: '16px',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center',
                }}
            >
                <Typography>
                    {t('No profiles added')}
                </Typography>
            </Box>
        )
    }


    const renderProfiles = () => {
        if (!open) return null

        return (
            profileIds.map((profileId) => {
                const profile = findProfileById(data ?? [], profileId)
                if (!profile) return null

                return (
                    <Accordion
                        key={profile.id}
                        expanded={expanded === profile.id}
                        onChange={handleChange(profile.id || '')}
                        disableGutters
                        sx={{
                            '&.MuiAccordion-root': {
                                flexDirection: 'column',
                                display: 'flex',
                            }
                        }}
                    >
                        <AccordionSummary
                            sx={{
                                backgroundColor: CommonThemeColors.sys.primary.main
                            }}
                            expandIcon={<ArrowDownwardIcon
                                sx={{color: CommonThemeColors.sys.primary.contrastText}}/>}
                        >
                            <Typography
                                sx={{color: CommonThemeColors.sys.primary.contrastText}}>
                                {getProfileFullName(profile)}
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails
                            sx={{
                                p: 0,
                                borderRadius: 0,
                                height: '100%',

                            }}
                        >
                            <ReservationProfile
                                profile={profile}
                                mode={'simple'}
                                reservationId={reservation?.id}
                                onDelete={() => handleDeleteProfile(profile)}
                                onChangeExpand={() => {
                                    setFullProfilePreview?.(profile)
                                }}
                                setProfile={() => refetch()}
                            />
                        </AccordionDetails>

                    </Accordion>
                )
            })
        )
    }

    const currentCount = profileIds.length ?? 0
    const atLimit = maxAllowedCount !== undefined && currentCount >= maxAllowedCount

    const renderNewSidebar = () => {
        if (!open) return null

        const profiles = profileIds
            .map(profileId => findProfileById(data ?? [], profileId))
            .filter((profile): profile is Profile => !!profile)
        const profile = profiles.find(guest => guest.id === expanded) ?? profiles[0]
        if (!profile) return null

        return (
            <ReservationProfile
                key={profile.id}
                profile={profile}
                mode={'simple'}
                reservationId={reservation?.id}
                onDelete={() => removeGuest(profile)}
                onChangeExpand={() => {
                    setFullProfilePreview?.(profile)
                }}
                setProfile={() => refetch()}
                otherGuests={{
                    profiles: profiles.filter(guest => guest !== profile),
                    onSelect: guest => selectGuest(guest.id!),
                    onOpenFull: guest => setFullProfilePreview?.(guest),
                    onRemove: readOnly ? undefined : removeGuest,
                    onAdd: readOnly || atLimit ? undefined : () => setSearchOpen(true)
                }}
            />
        )
    }

    // The full profile can edit the guest's own note, which lives on this booking too: take it over, or Save writes the old text back.
    const refreshGuestNote = async () => {
        if (!reservation?.id || !reservation.notes?.some(note => note.noteType === 'GUEST')) return
        const saved = await ReservationService.getReservationById(reservation.id)
        const guestNote = saved?.notes?.find(note => note.noteType === 'GUEST')
        if (guestNote) {
            setReservation?.({...reservation, notes: reservation.notes?.map(note => note.noteType === 'GUEST' ? guestNote : note)})
        }
    }

    const enteredOnline = <EnteredOnline reservation={reservation} guestIds={profileIds} guests={data}/>

    let content
    if (fullProfilePreview) {
        content = (
            <ReservationProfile
                profile={fullProfilePreview}
                mode={'full'}
                reservationId={reservation?.id}
                reservation={reservation}
                bookingDetails={enteredOnline}
                onClose={onClose}
                onDelete={() => removeGuest(fullProfilePreview)}
                onChangeExpand={() => {
                    setFullProfilePreview?.(undefined)
                }}
                setProfile={(profile) => {
                    refetch()
                    setFullProfilePreview?.(profile)
                    refreshGuestNote()
                }}
            />
        )
    } else if (searchOpen && showNewProfile) {
        content = (
            <GuestSearch
                guests={data ?? []}
                excludeIds={profileIds}
                onPick={pickGuest}
                onCreate={readOnly ? undefined : handleCreateNewGuest}
                onCancel={() => setSearchOpen(false)}
            />
        )
    } else if (searchOpen) {
        content = (
            <>
                <ProfileMatcher
                    excludeProfileId={profileIds || []}
                    onSelected={profile => {
                        if (!profile) return

                        const newProfiles = [...(profileIds || [])]
                        profile.id && newProfiles.push(profile.id)
                        setReservation?.({
                            ...reservation,
                            profileIds: newProfiles
                        })
                        addProfile?.(profile)
                        setSearchOpen(false)
                        setExpanded(profile?.id || '')
                        profile.id && onProfileSelected?.(profile.id)
                    }}
                    reservation={reservation}
                    onCreateNew={handleCreateNewGuest}
                    startWithAdvanced
                />

                <Button
                    variant={'contained'}
                    onClick={() => setSearchOpen(false)}
                    sx={{
                        borderRadius: 0,
                        mt: 'auto'
                    }}
                >
                    {t('Cancel')}
                </Button>
            </>
        )
    } else if (isFetching) {
        content = <Box sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%"
        }}>
            <CircularProgress/>
        </Box>
    } else if (data?.length === 0) {
        content = showNewProfile
            ? <NoGuests onAdd={readOnly ? undefined : () => setSearchOpen(true)}/>
            : renderNoProfilesUI()
    } else {
        content = showNewProfile ? renderNewSidebar() : renderProfiles()
    }

    return (
        <Stack
            sx={{
                flexDirection: 'column',
                borderRight: {xs: 'none', md: '1px solid #e0e0e0'},
                borderBottom: {xs: '1px solid #e0e0e0', md: 'none'},
                justifyContent: 'space-between',
                width: fullMode ? '100%' : {xs: '100%', md: showNewProfile ? 360 : 340},
                maxWidth: '100%',
                height: {xs: 'auto', md: '100%'}
            }}
        >
            <Stack ref={scrollRef} sx={{
                flexDirection: 'column',
                flex: 1,
                minHeight: 0,
                overflowY: {xs: 'visible', md: 'auto'},
                // The full profile scrolls inside (its sidebar and details on their own); this column then never does.
                ...(!fullMode && thinScrollbarSx),
            }}>
                {/* The new full profile shows it in its own column, under Back to reservation. */}
                {!(fullMode && showNewProfile) && (
                    <EnteredOnline reservation={reservation} guestIds={profileIds} guests={data} sx={{m: '16px 16px 0'}}/>
                )}
                {content}
            </Stack>

            {!fullMode && !searchOpen && (
                <Stack direction="column" spacing={1} sx={{p: atLimit ? 1 : 0}}>
                    {atLimit && (
                        <Alert severity="info" sx={{
                            textAlign: "center",
                            flexDirection: "column",
                            alignItems: "center"
                        }}>
                            <Typography variant="body2">
                                {t("This event only has capacity for {{count}} people.", {count: maxAllowedCount})}
                            </Typography>
                            <Typography variant="body2"
                                        sx={{textAlign: "center", mt: 0.5}}>
                                {t("Do you want to add more?")}
                            </Typography>
                            <Button
                                variant="outlined"
                                color="primary"
                                fullWidth
                                disabled={readOnly}
                                onClick={() => setSearchOpen(true)}
                                sx={{mt: 1}}
                            >
                                {t("Add extra profile")}
                            </Button>
                        </Alert>
                    )}

                    {/* The new sidebar has its own Add guest: under the guests, or in place of them. */}
                    {!showNewProfile && (
                        <Button
                            variant="contained"
                            fullWidth
                            sx={{borderRadius: 0}}
                            disabled={atLimit || readOnly}
                            onClick={() => setSearchOpen(true)}
                        >
                            {t("Add profile")}
                        </Button>
                    )}
                </Stack>
            )}

            <CommonConfirmDialog
                open={!!removing}
                title={t('Remove {{name}} from this reservation?', {name: removing ? getProfileFullName(removing) : ''})}
                message={t('The guest profile is kept.')}
                confirmLabel={t('Remove')}
                cancelLabel={t('Cancel')}
                confirmColor={'error'}
                onClose={() => setRemoving(undefined)}
                onConfirm={() => {
                    if (removing) handleDeleteProfile(removing)
                    setRemoving(undefined)
                }}
            />
        </Stack>
    )

}
export default ReservationProfiles;
